import { useEffect, useCallback } from 'react';
import { MatchState } from '../types';
import { ROLLBACK_CONFIG } from '../constants';
import * as matchStorage from '../storage/matchStorage';

export interface PersistedMatchData {
  matchState: MatchState;
  timestamp: number;
  matchId: string;
  progress: {
    isStarted: boolean;
    isFinished: boolean;
    currentScreen: string;
    pointsPlayed: number;
    gamesPlayed: number;
  };
}


function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Compresse une entrée d'historique (état précédent) au format de stockage.
 *
 * Source unique pour les trois chemins d'encodage de `hist` (sans limite, avec
 * limite, et fallback auto-limite) : ils ne diffèrent que par la tranche de
 * `state.history` à encoder, pas par la forme de chaque entrée.
 *
 * `gmc` = nombre de jeux terminés à CE moment de l'historique. Sans lui, la
 * restauration colle les setScores/gameResults FINAUX à chaque état d'historique
 * → jeu fantôme après rollback post-refresh.
 */
export function compressHistoryEntry(h: MatchState): Record<string, any> {
  return {
    gs: [h.gameScores.A, h.gameScores.B],
    sp: h.servingPlayer,
    ss: h.servingSide,
    ccs: h.canChangeServingSide,
    pts: h.pointsTable.length,
    gmc: h.gameResults.length,
    ...(h.pendingDecision && { pd: h.pendingDecision }),
    ...(h.pendingDecisionLineIndex !== null && { pdi: h.pendingDecisionLineIndex })
  };
}

/**
 * Hook pour gérer la persistance automatique du match
 */
export function useMatchPersistence(matchState: MatchState) {
  
  // Sauvegarder l'état actuel
  // ✅ VERSION OPTIMISÉE - Compression intelligente sans casser rollback
 const saveMatchState = useCallback((state: MatchState) => {
    try {
      // 🚀 COMPRESSION 1: Infos match optimisées (format court)
      const optimizedMatchInfo = {
        id: state.matchInfo.matchId,
        pA: state.matchInfo.playerA,
        pB: state.matchInfo.playerB,
        st: state.matchInfo.scoringType,
        // Infos complètes seulement si différentes des défauts
        ...(state.matchInfo.competitionName !== 'Compétition inconnue' && {
          comp: state.matchInfo.competitionName
        }),
        ...(state.matchInfo.refereeName !== 'Arbitre inconnu' && {
          ref: state.matchInfo.refereeName
        }),
        ...(state.matchInfo.courtName !== 'Court 1' && {
          court: state.matchInfo.courtName
        }),
        ...(state.matchInfo.categoryName !== 'Catégorie inconnue' && {
          cat: state.matchInfo.categoryName
        }),
        ...(state.matchInfo.drawName !== 'Match Simple' && {
          draw: state.matchInfo.drawName
        }),
        ...(state.matchInfo.resultCallbackUrl && {
          url: state.matchInfo.resultCallbackUrl
        }),
        // Livescoring fields
        ...(state.matchInfo.liveScoreUrl && {
          lsu: state.matchInfo.liveScoreUrl
        }),
        ...(state.matchInfo.context && {
          ctx: state.matchInfo.context
        }),
        ...(state.matchInfo.centerId && {
          ci: state.matchInfo.centerId
        }),
        ...(state.matchInfo.centerName && {
          cn: state.matchInfo.centerName
        }),
        ...(state.matchInfo.courtId && {
          ti: state.matchInfo.courtId
        }),
        // Avatars joueurs (URL, seulement si présents)
        ...(state.matchInfo.playerAAvatar && {
          pAv: state.matchInfo.playerAAvatar
        }),
        ...(state.matchInfo.playerBAvatar && {
          pBv: state.matchInfo.playerBAvatar
        })
      };

      // 🚀 COMPRESSION 2: État principal compact
      const compactState = {
        // Infos essentielles (noms de propriétés courts)
        mi: optimizedMatchInfo,
        cs: state.currentScreen,
        cA: state.playerAColor,
        cB: state.playerBColor,
        
        // Service (compact)
        sp: state.servingPlayer,
        ss: state.servingSide,
        ccs: state.canChangeServingSide,
        
        // Scores (arrays compacts au lieu d'objets)
        gs: [state.gameScores.A, state.gameScores.B],
        sets: [state.setScores.A, state.setScores.B],
        
        // État match
        started: state.matchStarted,
        ended: state.matchEnded,
        winner: state.winner,
        
        // Temps (essentiels pour la restauration)
        mst: state.matchStartTime,
        gst: state.gameStartTime,
        tmd: state.totalMatchDuration,
        
        // 📋 Points (ultra compact - arrays de arrays)
        pts: state.pointsTable.map(p => [
          p.playerA,
          p.playerB,
          p.servingPlayer,
          p.servingSide,
          p.decision || '',
          p.decisionFor || null // Index 5: joueur concerné par la décision
        ]),
        
        // 🎯 Jeux (compact avec flags combinés)
        gms: state.gameResults.map(g => [
          g.winner, 
          g.scoreA, 
          g.scoreB, 
          g.duration,
          (g.wonByConduct ? 1 : 0) + (g.wonByInjury ? 2 : 0) // Flags combinés en 1 nombre
        ]),
        
        // ⚖️ Sanctions (seulement si présentes)
        ...(state.conductSanctions.length > 0 && {
          scs: state.conductSanctions.map(s => [
            s.player, 
            s.type, 
            s.justification,
            s.timestamp
          ])
        }),
        
        // 🔄 HISTORIQUE COMPRESSÉ avec limite configurable
        hist: (() => {
          const configuredLimit = ROLLBACK_CONFIG.MAX_ROLLBACKS_IN_STORAGE;
          const maxRollbacks = configuredLimit !== null ? configuredLimit : 15; // Fallback sûr
          
          // Si pas de limite configurée, garder tous les rollbacks
          if (maxRollbacks === null) {
            return state.history.map(compressHistoryEntry);
          }

          // Limite configurée - garder les X derniers
          return state.history.slice(-maxRollbacks).map(compressHistoryEntry);
        })()
      };

      const persistedData: PersistedMatchData = {
        matchState: compactState as any, // Cast temporaire pour compatibilité
        timestamp: Date.now(),
        matchId: state.matchInfo.matchId,
        progress: {
          isStarted: state.matchStarted,
          isFinished: state.matchEnded,
          currentScreen: state.currentScreen,
          pointsPlayed: state.pointsTable.length,
          gamesPlayed: state.gameResults.length
        }
      };

      // 🧠 PROTECTION INTELLIGENTE : Vérifier la taille avant sauvegarde
      const jsonString = JSON.stringify(persistedData);
      const sizeKB = jsonString.length / 1024;

      // Si le storage devient trop gros et qu'on n'a pas de limite, activer une limite automatique
      if (sizeKB > ROLLBACK_CONFIG.AUTO_LIMIT_THRESHOLD_KB && ROLLBACK_CONFIG.MAX_ROLLBACKS_IN_STORAGE === null) {
        console.warn(`⚠️ Storage getting large (${sizeKB.toFixed(1)}KB), applying automatic rollback limit`);
        
        // Recalculer avec limite de fallback
        const limitedCompactState = {
          ...compactState,
          hist: state.history.slice(-ROLLBACK_CONFIG.FALLBACK_LIMIT).map(compressHistoryEntry)
        };
        
        const limitedPersistedData = {
          ...persistedData,
          matchState: limitedCompactState
        };
        
        matchStorage.saveMatchState(limitedPersistedData);

      } else {
        // Taille acceptable - sauvegarder normalement
        matchStorage.saveMatchStateRaw(jsonString);
      }
    } catch (error) {
      console.warn('Erreur compression intelligente:', error);
      // Fallback vers sauvegarde minimale en cas d'erreur
      const fallbackData = {
        matchState: { 
          matchId: state.matchInfo.matchId,
          emergency: true,
          timestamp: Date.now()
        },
        timestamp: Date.now(),
        matchId: state.matchInfo.matchId,
        progress: { isStarted: false, isFinished: false, currentScreen: 'before', pointsPlayed: 0, gamesPlayed: 0 }
      };
      matchStorage.saveMatchState(fallbackData);
    }
  }, []);

  // 🚀 ACTIVATION du debouncing existant
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce(saveMatchState, 500), // 500ms de délai groupé
    [saveMatchState]
  );

  // Sauvegarder automatiquement à chaque changement d'état significatif
  useEffect(() => {
    // Sauvegarder seulement si le match a démarré
    if (matchState.matchStarted || matchState.pointsTable.length > 0 || matchState.gameResults.length > 0) {
      debouncedSave(matchState);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    matchState.pointsTable.length,
    matchState.gameResults.length,
    matchState.gameScores,
    matchState.setScores,
    matchState.currentScreen,
    matchState.matchEnded,
    debouncedSave
    // matchState omis intentionnellement - on ne veut pas sauvegarder à chaque micro-changement
  ]);

  // Nettoyer toutes les données de match (état + payload de lancement + QR)
  const clearSavedMatch = useCallback(() => {
    matchStorage.clearAll();
  }, []);

  // ✅ RETURN OBLIGATOIRE
  return {
    saveMatchState,
    clearSavedMatch
  };
}

/**
 * ✅ Décompresse un état compressé en MatchState complet
 * Reconstruit toutes les données pour compatibilité avec l'app
 */
const expandSmartCompressed = (compact: any): MatchState => {
  // Reconstituer le matchInfo complet à partir des données compressées
  const fullMatchInfo = {
    matchId: compact.mi.id,
    competitionName: compact.mi.comp || 'Compétition inconnue',
    categoryName: compact.mi.cat || 'Catégorie inconnue',
    drawName: compact.mi.draw || 'Match Simple',
    refereeName: compact.mi.ref || 'Arbitre inconnu',
    dateTime: compact.mi.dateTime || new Date().toISOString().slice(0, 16).replace('T', ' '),
    courtName: compact.mi.court || 'Court 1',
    scoringType: compact.mi.st,
    playerA: compact.mi.pA,
    playerB: compact.mi.pB,
    resultCallbackUrl: compact.mi.url,
    // Livescoring fields
    ...(compact.mi.lsu && { liveScoreUrl: compact.mi.lsu }),
    ...(compact.mi.ctx && { context: compact.mi.ctx }),
    ...(compact.mi.ci && { centerId: compact.mi.ci }),
    ...(compact.mi.cn && { centerName: compact.mi.cn }),
    ...(compact.mi.ti && { courtId: compact.mi.ti }),
    // Avatars joueurs (rétrocompat : absents des états sauvegardés avant cette feature)
    ...(compact.mi.pAv && { playerAAvatar: compact.mi.pAv }),
    ...(compact.mi.pBv && { playerBAvatar: compact.mi.pBv }),
  };

  // Reconstituer l'historique COMPLET pour rollback multiple
  const fullHistory: MatchState[] = (compact.hist || []).map((h: any, index: number) => {
    // Jeux réellement terminés à CE moment de l'historique.
    // Fallback rétrocompat : les états sauvegardés avant l'ajout de `gmc`
    // n'ont pas ce champ → on retombe sur l'ancien comportement (tous les jeux).
    const allGames = compact.gms || [];
    const gmc = h.gmc !== undefined ? h.gmc : allGames.length;
    const playedGames = allGames.slice(0, gmc);

    // setScores dérivés des jeux réellement joués (invariant : 1 set gagné = 1 game)
    const histSetScores = {
      A: playedGames.filter((g: any[]) => g[0] === 'A').length,
      B: playedGames.filter((g: any[]) => g[0] === 'B').length
    };

    return {
    matchInfo: fullMatchInfo,
    currentScreen: compact.cs,
    playerAColor: compact.cA,
    playerBColor: compact.cB,
    servingPlayer: h.sp,
    servingSide: h.ss,
    canChangeServingSide: h.ccs,
    gameScores: { A: h.gs[0], B: h.gs[1] },
    setScores: histSetScores,
    matchStarted: compact.started,
    matchEnded: false, // États historiques ne sont jamais terminés
    winner: null,
    history: [], // Éviter récursion infinie dans l'historique
    // Reconstituer le tableau de points jusqu'à l'index de cet état historique
    pointsTable: (compact.pts || []).slice(0, h.pts).map((p: any[]) => ({
      playerA: p[0],
      playerB: p[1],
      servingPlayer: p[2],
      servingSide: p[3],
      decision: p[4] || '',
      decisionFor: p[5] || null
    })),
    pendingDecision: h.pd || null,
    pendingDecisionLineIndex: h.pdi !== undefined ? h.pdi : null,
    matchStartTime: compact.mst,
    gameStartTime: compact.gst,
    totalMatchDuration: compact.tmd || 0,
    currentGameDuration: 0,
    gameResults: playedGames.map((g: any[]) => ({
      winner: g[0] as 'A' | 'B',
      scoreA: g[1],
      scoreB: g[2],
      duration: g[3],
      wonByConduct: (g[4] & 1) === 1, // Bit 0 pour conduct
      wonByInjury: (g[4] & 2) === 2   // Bit 1 pour injury
    })),
    conductSanctions: [] // Pas besoin dans l'historique
    };
  });

  // Reconstituer l'état principal complet
  return {
    matchInfo: fullMatchInfo,
    currentScreen: compact.cs,
    playerAColor: compact.cA,
    playerBColor: compact.cB,
    servingPlayer: compact.sp,
    servingSide: compact.ss,
    canChangeServingSide: compact.ccs,
    gameScores: { A: compact.gs[0], B: compact.gs[1] },
    setScores: { A: compact.sets[0], B: compact.sets[1] },
    matchStarted: compact.started,
    matchEnded: compact.ended,
    winner: compact.winner,
    history: fullHistory, // ✅ ROLLBACK MULTIPLE PRÉSERVÉ
    pointsTable: (compact.pts || []).map((p: any[]) => ({
      playerA: p[0],
      playerB: p[1],
      servingPlayer: p[2],
      servingSide: p[3],
      decision: p[4] || '',
      decisionFor: p[5] || null
    })),
    pendingDecision: null,
    pendingDecisionLineIndex: null,
    matchStartTime: compact.mst,
    gameStartTime: compact.gst,
    totalMatchDuration: compact.tmd || 0,
    currentGameDuration: 0,
    gameResults: (compact.gms || []).map((g: any[]) => ({
      winner: g[0] as 'A' | 'B',
      scoreA: g[1],
      scoreB: g[2], 
      duration: g[3],
      wonByConduct: (g[4] & 1) === 1,
      wonByInjury: (g[4] & 2) === 2
    })),
    conductSanctions: (compact.scs || []).map((s: any[]) => ({
      type: s[1],
      player: s[0] as 'A' | 'B',
      justification: s[2],
      timestamp: s[3] || Date.now(),
      gameNumber: 1,
      gameScore: { A: compact.gs[0], B: compact.gs[1] }
    }))
  };
};

/**
 * Vérifier s'il y a un match sauvegardé
 */
export function checkForSavedMatch(): PersistedMatchData | null {
  // Lecture, péremption 24h et purge des données corrompues : voir matchStorage
  return matchStorage.readMatchState();
}

/**
 * Restaurer un match sauvegardé
 */
export function restoreSavedMatch(): MatchState | null {
  const saved = checkForSavedMatch();
  if (!saved) return null;
  
  try {
    // ✅ Utiliser any temporairement pour éviter les erreurs TypeScript
    const savedState = saved.matchState as any;
    
    // ✅ Détecter le format des données sauvegardées
    //if (savedState.mi && savedState.gs) {
    //  // Nouveau format compressé - décompresser
      return expandSmartCompressed(savedState);
    //} else if (savedState.matchInfo && savedState.gameScores) {
    // // Ancien format non compressé - utiliser tel quel
    //  console.log('🔄 Restoring from legacy format');
    //  return savedState as MatchState;
    //} else {
    //  console.warn('⚠️ Unknown saved match format');
    //  return null;
    //}
  } catch (error) {
    console.error('❌ Error restoring saved match:', error);
    // En cas d'erreur, nettoyer les données corrompues
    matchStorage.clearMatchState();
    return null;
  }
}

/**
 * Hook pour la prévention du refresh pendant un match
 */
export function useRefreshProtection(isMatchActive: boolean) {
  useEffect(() => {
    if (!isMatchActive) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Message d'avertissement avant de quitter
      event.preventDefault();
      event.returnValue = 'Un match est en cours. Êtes-vous sûr de vouloir quitter ?';
      return event.returnValue;
    };

    const handleUnload = () => {
      // Sauvegarder une dernière fois avant de partir
      console.log('Sauvegarde d\'urgence avant fermeture');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [isMatchActive]);
}